/**
 * Tuxedo builder (Option A)
 * ---------------------------------------------------------------------------
 * One page to rent OR buy a full tuxedo (jacket / shirt / trousers).
 *  - RENT: pick dates first (the reused site date picker, driven here since the
 *    shared rental-block.js is single-product), sizes are then filtered to what's
 *    available for those dates; on add, one rental hold per component.
 *  - BUY: the date picker hides; sizes come from each piece's resale price; on
 *    add, one resale hold per component at the buy price.
 * Either way all three pieces are added as separate grouped cart lines, each with
 * its own variant id + hold, so BTS tracks each as its own item.
 *
 * Changing the dates recalculates availability WITHOUT clearing chosen sizes.
 * Reuses the single-item hold contract + date rules (rental-api / resale-ops /
 * rental-config / rental-helpers).
 */
import { createRentalHold, expireRentalHold, checkVariantAvailability } from './rental-api.js';
import { createResaleHold, expireResaleHold } from './resale-ops.js';
import { RENTAL_RULES, ERROR_MESSAGES } from './rental-config.js';
import { calculateRentalDays, formatDate, formatPrice, fetchRentalFeePreview } from './rental-helpers.js';
import { getApiUrl } from './api-config.js';

const root = document.querySelector('[data-tuxedo-builder]');
if (root) initTuxedoBuilder(root);

function initTuxedoBuilder(root) {
  const apiUrl = getApiUrl('');
  const componentsWrap = root.querySelector('[data-tux-components]');
  const checkingEl = root.querySelector('[data-tux-checking]');
  const lockNote = root.querySelector('[data-tux-lock-note]');
  const summary = root.querySelector('[data-tux-summary]');
  const linesEl = root.querySelector('[data-tux-lines]');
  const totalEl = root.querySelector('[data-tux-total]');
  const errorEl = root.querySelector('[data-tux-error]');
  const addBtn = root.querySelector('[data-tux-add]');
  const addText = root.querySelector('[data-tux-add-text]');
  const dateBlock = root.querySelector('.rental-dates-block');
  const datesWrap = root.querySelector('.tuxedo-builder__dates-wrap');
  const rentRadio = root.querySelector('[data-rent-item-radio]');
  const buyRadio = root.querySelector('[data-buy-item-radio]');

  if (!addBtn || !componentsWrap) return;

  const components = Array.from(componentsWrap.querySelectorAll('[data-tux-component]')).map((el) => {
    let variants = [];
    try { variants = JSON.parse(el.dataset.tuxVariants || '[]'); } catch (e) { variants = []; }
    return {
      key: el.dataset.tuxComponent,
      label: el.dataset.tuxLabel || el.dataset.tuxComponent,
      el,
      variants,
      optionNames: Array.from(el.querySelectorAll('[data-tux-option]')).map((f) => f.dataset.tuxOption),
      chips: Array.from(el.querySelectorAll('[data-tux-chip]')),
      toggle: el.querySelector('[data-tux-toggle]'),
      body: el.querySelector('[data-tux-body]'),
      selectedEl: el.querySelector('[data-tux-selected]'),
      selected: {},
      availableIds: new Set(),
      checkedKey: null
    };
  });
  const termsCheckbox = root.querySelector('[data-rental-terms-checkbox]');

  let mode = 'rent';
  let checksInFlight = 0;
  const state = { start: null, end: null, days: null };

  const showError = (msg) => { if (!errorEl) return; errorEl.textContent = msg || ''; errorEl.classList.toggle('hidden', !msg); };
  const datesValid = () => state.start && state.end && state.days >= RENTAL_RULES.MIN_RENTAL_DAYS && state.days <= RENTAL_RULES.MAX_RENTAL_DAYS;

  const resolve = (c) => {
    if (c.optionNames.some((n) => !c.selected[n])) return null;
    const match = c.variants.find((v) => (mode === 'buy' ? v.resale : v.rent) && c.optionNames.every((n) => v.opts[n] === c.selected[n]));
    if (!match) return null;
    return { id: match.id, available: mode === 'buy' ? true : c.availableIds.has(match.id), resalePrice: match.resalePrice };
  };
  // A piece can be left out entirely. "Selected" = all its options chosen;
  // "partial" = some but not all (must be finished or cleared before adding).
  const isSelected = (c) => c.optionNames.length > 0 && c.optionNames.every((n) => c.selected[n]);
  const isPartial = (c) => c.optionNames.some((n) => c.selected[n]) && !isSelected(c);
  const selectedComps = () => components.filter(isSelected);
  const chosenAvailable = () => selectedComps().every((c) => { const r = resolve(c); return r && r.available; });
  const isReady = () =>
    (mode === 'buy' ? true : datesValid()) &&
    selectedComps().length >= 1 &&
    !components.some(isPartial) &&
    chosenAvailable();

  // Enable/grey chips for the active mode. Never touches `selected` — selections persist.
  function applyChipStates() {
    components.forEach((c) => {
      c.chips.forEach((chip) => {
        const optName = chip.dataset.tuxOptionName, val = chip.dataset.tuxValue;
        const enabled = mode === 'buy'
          ? c.variants.some((v) => v.resale && v.opts[optName] === val)
          : datesValid() && c.variants.some((v) => v.rent && v.opts[optName] === val && c.availableIds.has(v.id));
        const i = chip.querySelector('input');
        if (i) i.disabled = !enabled;
        chip.classList.toggle('is-unavailable', !enabled);
      });
    });
  }

  // ---- Date picker: reuse rental-calendar markup, drive it ourselves ------
  let fp = null;
  if (dateBlock) {
    const startTrigger = dateBlock.querySelector('.rental-date-trigger[data-mode="start"]');
    const endTrigger = dateBlock.querySelector('.rental-date-trigger[data-mode="end"]');
    const startDisplay = dateBlock.querySelector('.rental-start-display');
    const endDisplay = dateBlock.querySelector('.rental-end-display');
    const panel = dateBlock.querySelector('.rental-calendar-panel');
    const calDiv = dateBlock.querySelector('.rental-calendar');
    const hint = dateBlock.querySelector('.rental-date-hint');
    const clearBtn = dateBlock.querySelector('.rental-date-clear');
    const doneBtn = dateBlock.querySelector('.rental-date-done');
    const hiddenStart = dateBlock.querySelector('input[name="rental_start_date"]');
    const hiddenEnd = dateBlock.querySelector('input[name="rental_end_date"]');

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const minDate = new Date(today); minDate.setDate(today.getDate() + RENTAL_RULES.NON_ENDLESS_BOOKING_LEAD_DAYS);
    const maxDate = new Date(today); maxDate.setDate(today.getDate() + RENTAL_RULES.BOOKING_WINDOW_DAYS);
    const fmtDisplay = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    const openPanel = () => panel && panel.classList.add('open');
    const closePanel = () => {
      panel && panel.classList.remove('open');
      startTrigger && startTrigger.classList.remove('active');
      endTrigger && endTrigger.classList.remove('active');
    };
    const setActive = (n) => {
      startTrigger && startTrigger.classList.toggle('active', n === 0);
      endTrigger && endTrigger.classList.toggle('active', n === 1);
    };
    const setHint = (html, alert) => {
      if (!hint) return;
      hint.innerHTML = html || '';
      hint.classList.toggle('hidden', !html);
      hint.classList.toggle('rental-date-hint--alert', !!alert);
    };

    const initPicker = () => {
      if (typeof window.flatpickr !== 'function' || !calDiv) return false;
      const hidden = document.createElement('input');
      hidden.type = 'text'; hidden.style.display = 'none';
      calDiv.appendChild(hidden);
      fp = window.flatpickr(hidden, {
        inline: true, appendTo: calDiv, mode: 'range',
        showMonths: window.matchMedia('(min-width: 750px)').matches ? 2 : 1,
        dateFormat: 'd-m-Y', minDate, maxDate, locale: { firstDayOfWeek: 1 },
        onReady: (sel, str, inst) => inst.calendarContainer.classList.add('endless-fp'),
        onChange: (dates) => onCalChange(dates)
      });
      return true;
    };
    if (!initPicker()) window.addEventListener('load', initPicker, { once: true });

    startTrigger && startTrigger.addEventListener('click', openPanel);
    endTrigger && endTrigger.addEventListener('click', openPanel);
    doneBtn && doneBtn.addEventListener('click', closePanel);
    clearBtn && clearBtn.addEventListener('click', () => { if (fp) fp.clear(); });
    document.addEventListener('click', (e) => { if (!dateBlock.contains(e.target)) closePanel(); });

    function onCalChange(dates) {
      if (dates[0]) { startDisplay.textContent = fmtDisplay(dates[0]); startDisplay.classList.remove('empty'); }
      else { startDisplay.textContent = 'Select date'; startDisplay.classList.add('empty'); }
      if (dates[1]) { endDisplay.textContent = fmtDisplay(dates[1]); endDisplay.classList.remove('empty'); }
      else { endDisplay.textContent = 'Select date'; endDisplay.classList.add('empty'); }
      setActive(dates.length === 0 ? 0 : dates.length === 1 ? 1 : -1);
      doneBtn && doneBtn.classList.toggle('is-ready', dates.length === 2);

      if (dates.length < 2) {
        setHint(dates.length === 1 ? 'Now select your pick-up date' : 'Select your drop-off date');
        state.start = state.end = state.days = null;
        if (hiddenStart) hiddenStart.value = ''; if (hiddenEnd) hiddenEnd.value = '';
        if (mode === 'rent') componentsWrap.classList.add('is-locked');
        refresh();
        return;
      }
      const start = formatDate(dates[0]);
      const end = formatDate(dates[1]);
      const days = calculateRentalDays(start, end);
      if (days < RENTAL_RULES.MIN_RENTAL_DAYS) { setHint(ERROR_MESSAGES.TOO_SHORT, true); state.days = null; if (mode === 'rent') componentsWrap.classList.add('is-locked'); refresh(); return; }
      if (days > RENTAL_RULES.MAX_RENTAL_DAYS) { setHint(ERROR_MESSAGES.TOO_LONG, true); state.days = null; if (mode === 'rent') componentsWrap.classList.add('is-locked'); refresh(); return; }
      setHint(`<span class="rental-date-hint__days">${days} day${days === 1 ? '' : 's'}</span>`);
      state.start = start; state.end = end; state.days = days;
      if (hiddenStart) hiddenStart.value = start;
      if (hiddenEnd) hiddenEnd.value = end;
      closePanel(); // both dates chosen — close the calendar automatically
      if (mode === 'rent') onDatesReady(); // lazy: revalidate chosen pieces; others checked on open
    }
  }

  // ---- Availability (rent mode): lazy + cached per component -------------
  // The API only checks one variant at a time, so instead of pre-checking every
  // size on each date change (~31 calls), we check a piece's sizes only when its
  // row is opened (cached per date range), and on a date change we re-validate
  // just the pieces already chosen (one call each).
  const dateKey = () => (state.start && state.end) ? state.start + '|' + state.end : '';
  const checkVariant = (id) =>
    checkVariantAvailability(id, state.start, state.end, apiUrl)
      .then((r) => !!(r && r.success && r.count > 0)).catch(() => false);

  async function checkComponentFull(c) {
    if (!datesValid() || c.checkedKey === dateKey() || c.checking) return;
    c.checking = true;
    checksInFlight++; if (checkingEl) checkingEl.classList.remove('hidden');
    try {
      const myKey = dateKey();
      const rentables = c.variants.filter((v) => v.rent);
      const results = await Promise.all(rentables.map((v) => checkVariant(v.id).then((ok) => ({ id: v.id, ok }))));
      if (dateKey() === myKey) { // ignore if dates changed mid-flight
        c.availableIds = new Set(results.filter((r) => r.ok).map((r) => r.id));
        c.checkedKey = myKey;
        applyChipStates();
        refresh();
      }
    } finally {
      c.checking = false;
      checksInFlight = Math.max(0, checksInFlight - 1);
      if (checksInFlight === 0 && checkingEl) checkingEl.classList.add('hidden');
    }
  }

  function onDatesReady() {
    componentsWrap.classList.remove('is-locked');
    showError('');
    components.forEach((c) => { c.checkedKey = null; c.availableIds = new Set(); c.checking = false; });
    applyChipStates(); // greyed until the checks return
    refresh();
    // Preload every piece's availability in the background (sequential = gentle on the
    // API, one date range at a time, cached), so opening a piece is instant.
    (async () => { for (const c of components) { await checkComponentFull(c); } })();
  }

  // ---- Mode (rent / buy) -------------------------------------------------
  function updateGate() {
    if (addText) addText.textContent = mode === 'buy' ? 'Add tuxedo to cart' : 'Add tuxedo to cart';
    if (mode === 'buy') {
      if (datesWrap) datesWrap.style.display = 'none';
      if (lockNote) lockNote.style.display = 'none';
      if (checkingEl) checkingEl.classList.add('hidden');
      componentsWrap.classList.remove('is-locked');
      applyChipStates();
      refresh();
    } else {
      if (datesWrap) datesWrap.style.display = '';
      if (lockNote) lockNote.style.display = '';
      if (datesValid()) {
        onDatesReady();
      } else {
        componentsWrap.classList.add('is-locked');
        refresh();
      }
    }
  }
  const applyMode = () => { mode = buyRadio && buyRadio.checked ? 'buy' : 'rent'; updateGate(); };
  rentRadio && rentRadio.addEventListener('change', applyMode);
  buyRadio && buyRadio.addEventListener('change', applyMode);

  // ---- Accordion (collapse to a row, tap to reveal sizes) ---------------
  const collapse = (c) => { if (c.body) { c.body.setAttribute('hidden', ''); c.toggle && c.toggle.setAttribute('aria-expanded', 'false'); } };
  const expand = (c) => { if (c.body) { c.body.removeAttribute('hidden'); c.toggle && c.toggle.setAttribute('aria-expanded', 'true'); } };
  function updateComponentSummary(c) {
    const chosen = c.optionNames.map((n) => c.selected[n]).filter(Boolean);
    const complete = c.optionNames.length > 0 && c.optionNames.every((n) => c.selected[n]);
    if (c.selectedEl) c.selectedEl.textContent = chosen.length ? chosen.join(' / ') : defaultSummary();
    c.el.classList.toggle('is-selected', complete);
    if (complete) collapse(c); // once fully chosen, tuck it away
  }
  // What an un-chosen row shows: in rent mode before dates it must read as gated.
  function defaultSummary() {
    return (mode === 'rent' && !datesValid()) ? 'Select dates first' : 'Select size';
  }
  function updateLockLabels() {
    components.forEach((c) => { if (!isSelected(c) && c.selectedEl) c.selectedEl.textContent = defaultSummary(); });
  }
  components.forEach((c) => {
    c.toggle && c.toggle.addEventListener('click', () => {
      const wasOpen = c.body && !c.body.hasAttribute('hidden');
      components.forEach(collapse); // one open at a time
      if (!wasOpen) {
        expand(c);
        if (mode === 'rent') checkComponentFull(c); // check this piece's sizes on open (cached)
      }
    });
    // ---- Chip selection ----
    c.el.addEventListener('change', (e) => {
      const input = e.target.closest('input[type="radio"]');
      if (!input || input.disabled) return;
      const chip = input.closest('[data-tux-chip]');
      if (!chip) return;
      c.selected[chip.dataset.tuxOptionName] = chip.dataset.tuxValue;
      updateComponentSummary(c);
      refresh();
    });
    // Click an already-chosen chip to deselect it — lets a customer drop a piece
    // (take one or two, not all three) or clear a half-finished one.
    c.chips.forEach((chip) => {
      const input = chip.querySelector('input');
      if (!input) return;
      input.addEventListener('click', () => {
        if (input.disabled) return;
        const optName = chip.dataset.tuxOptionName;
        if (c.selected[optName] === chip.dataset.tuxValue) {
          input.checked = false;
          delete c.selected[optName];
          updateComponentSummary(c);
          refresh();
        }
      });
    });
  });

  // ---- Price preview + readiness ----------------------------------------
  async function refresh() {
    showError('');
    updateLockLabels();
    // Surface a chosen piece that isn't available for the dates (rent only).
    if (mode === 'rent' && datesValid()) {
      const unavailable = selectedComps().filter((c) => { const r = resolve(c); return r && !r.available; });
      if (unavailable.length) showError(`Not available for these dates: ${unavailable.map((c) => c.label).join(', ')}. Try different dates or sizes.`);
    }
    if (!isReady()) {
      if (summary) summary.hidden = true;
      addBtn.disabled = true;
      return;
    }
    // Only the pieces actually chosen — a customer can take one, two or all three.
    const resolved = selectedComps().map((c) => ({ c, r: resolve(c) }));
    let previews = [];
    if (mode === 'rent') previews = await Promise.all(resolved.map((x) => fetchRentalFeePreview(x.r.id, state.days)));
    let total = 0;
    if (linesEl) linesEl.innerHTML = '';
    resolved.forEach((x, i) => {
      let amount = null;
      if (mode === 'buy') { const p = Number(x.r.resalePrice); amount = isNaN(p) ? null : p; }
      else amount = previews[i] && typeof previews[i].total === 'number' ? previews[i].total : null;
      if (amount != null) total += amount;
      const sizeText = x.c.optionNames.map((n) => x.c.selected[n]).join(' / ');
      if (linesEl) {
        const li = document.createElement('li');
        li.className = 'tuxedo-builder__line';
        li.innerHTML = `<span>${x.c.label} <em>${sizeText}</em></span><span>${amount != null ? '\u20C3' + formatPrice(amount) : '—'}</span>`;
        linesEl.appendChild(li);
      }
    });
    if (totalEl) totalEl.textContent = '\u20C3' + formatPrice(total);
    if (summary) summary.hidden = false;
    addBtn.disabled = false;
  }

  // ---- Add to cart: 3 holds -> 1 grouped multi-line add -----------------
  addBtn.addEventListener('click', async () => {
    if (!isReady()) return;
    if (mode === 'rent' && termsCheckbox && !termsCheckbox.checked) {
      showError('Please accept the rental terms & conditions to continue.');
      return;
    }
    showError('');
    addBtn.classList.add('is-adding');
    if (addText) addText.textContent = 'Adding to cart…';
    addBtn.disabled = true;
    const created = [];
    try {
      for (const c of selectedComps()) {
        const variantId = resolve(c).id;
        let hold;
        if (mode === 'buy') hold = await createResaleHold({}, variantId, apiUrl);
        else hold = await createRentalHold({ start_date: state.start, end_date: state.end, days: state.days }, variantId, apiUrl);
        const price = mode === 'buy' ? hold && hold.resalePrice : hold && hold.rentalPrice;
        if (!hold || !hold.holdUid || !hold.priceSignature || price == null) {
          throw new Error((hold && hold.error) || `${c.label}: ${ERROR_MESSAGES.UNABLE_TO_RESERVE}`);
        }
        created.push({ c, variantId, hold });
      }
      const groupId = 'TUX-' + Date.now().toString(36).toUpperCase();
      const items = created.map(({ c, variantId, hold }) => {
        let properties;
        if (mode === 'buy') {
          properties = {
            _is_resale: 'true',
            _resale_price: String(hold.resalePrice),
            _resale_hold_uid: hold.holdUid,
            _resale_signature: hold.priceSignature,
            _purchase_tag: 'Resale',
            _tuxedo_group_id: groupId,
            _tuxedo_component: c.label
          };
        } else {
          properties = {
            _is_rental: 'true',
            _rental_price: hold.rentalPrice,
            _rental_start_date: state.start,
            _rental_end_date: state.end,
            _rental_days: String(state.days),
            _rental_hold_uid: hold.holdUid,
            _rental_signature: hold.priceSignature,
            'Terms accepted': 'Yes',
            _tuxedo_group_id: groupId,
            _tuxedo_component: c.label
          };
          if (hold.basePrice != null) properties._rental_base_price = String(hold.basePrice);
          if (Array.isArray(hold.customerFeesSnapshot) && hold.customerFeesSnapshot.length) properties._rental_fees_bundle = JSON.stringify(hold.customerFeesSnapshot);
        }
        return { id: Number(variantId), quantity: 1, properties };
      });
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) { const data = await res.json().catch(() => ({})); throw new Error(data.description || data.message || 'Could not add to cart.'); }
      window.location.href = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
    } catch (err) {
      await Promise.all(created.map(({ hold }) => {
        const fn = mode === 'buy' ? expireResaleHold : expireRentalHold;
        return fn(hold.holdUid, apiUrl, 'tuxedo_add_failed').catch(() => {});
      }));
      showError(err && err.message ? err.message : ERROR_MESSAGES.UNABLE_TO_RESERVE);
      addBtn.classList.remove('is-adding');
      if (addText) addText.textContent = 'Add tuxedo to cart';
      addBtn.disabled = false;
    }
  });

  // Initial state (rent, no dates → locked).
  updateGate();
}
